import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Generic ResizeObserver hook for tracking element size changes
 *
 * @param {React.RefObject} elementRef - Ref to the element to observe
 * @param {Function} callback - Callback function called when element resizes
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether the observer is active
 * @param {AbortController} options.abortController - Optional abort controller for cleanup
 * @returns {Object} - Observer control methods
 * @author Agent FAX8TXL
 */
export const useResizeObserver = (elementRef, callback, options = {}) => {
  const {
    enabled = true,
    abortController = null,
  } = options;

  const observerRef = useRef(null);
  const callbackRef = useRef(callback);
  const [error, setError] = useState(null);
  const [isObserving, setIsObserving] = useState(false);

  // Keep callback reference fresh
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const observe = useCallback(() => {
    if (!elementRef?.current || !enabled) {
      setIsObserving(false);
      return undefined;
    }

    try {
      const element = elementRef.current;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new ResizeObserver((entries) => {
        if (callbackRef.current) {
          callbackRef.current(entries, element);
        }
      });

      observerRef.current.observe(element);
      setError(null);
      setIsObserving(true);
      return observerRef.current;
    } catch (err) {
      setError(err);
      setIsObserving(false);
      return undefined;
    }
  }, [elementRef, enabled]);

  const disconnect = useCallback(() => {
    try {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      setError(null);
      setIsObserving(false);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return undefined;
    }

    // Wrap observe call in try-catch to handle errors gracefully
    try {
      observe();
    } catch (err) {
      setError(err);
      setIsObserving(false);
    }

    // Handle abort controller cleanup
    const handleAbort = () => disconnect();
    if (abortController?.signal) {
      abortController.signal.addEventListener('abort', handleAbort);
    }

    return () => {
      disconnect();
      if (abortController?.signal) {
        abortController.signal.removeEventListener('abort', handleAbort);
      }
    };
  }, [enabled, observe, disconnect, abortController]);

  return {
    observe,
    disconnect,
    isObserving,
    error,
  };
};